import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/pages/UploadPhoto.css';
import '../styles/pages/StepColorOverrides.css';
import '../styles/components/FixedStepper.css';
import '../styles/pages/UploadPhotoOverrides.css';
import '../styles/pages/UploadStepperFix.css'; // Added for stepper alignment on mobile
import '../styles/pages/UploadPhotoMobileFixes.css'; // Added for mobile font size and modal fixes
import axios from 'axios';
import Loader from '../components/Loader';
import ReactCrop, { centerCrop, makeAspectCrop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import sessionTracker from '../utils/sessionTracker'; // Import session tracker
import { uploadOriginalImageToS3, uploadCroppedPreviewToS3 } from '../utils/s3Upload'; // Import S3 upload functions

const UploadPhoto = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processedImage, setProcessedImage] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [isNormalSelfie, setIsNormalSelfie] = useState(false);
  const [isSelfieMode, setIsSelfieMode] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Add poster aspect ratio first, before any other state references it
  const [posterAspectRatio] = useState(0.56); // 9:16 aspect ratio (width/height)

  // Add state variables for React Crop
  const [crop, setCrop] = useState();
  const [aspect, setAspect] = useState(posterAspectRatio); // Now posterAspectRatio is defined first
  const [completedCrop, setCompletedCrop] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [isCropComplete, setIsCropComplete] = useState(false);
  const [useFixedAspectRatio, setUseFixedAspectRatio] = useState(true);
  const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 0, height: 0 });
  const [imageOrientation, setImageOrientation] = useState('portrait'); // 'portrait' or 'landscape'
  const [showCropModal, setShowCropModal] = useState(false);
  const [showFixedRatioBox] = useState(true);
  const [cropData, setCropData] = useState(null);

  // Add showCameraModal state
  const [showCameraModal, setShowCameraModal] = useState(false);

  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const detectionRef = useRef(null);
  const navigate = useNavigate();

  

  // Add resize listener to update mobile state
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      
      setIsMobile(newIsMobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to check if image is portrait or landscape
  const detectImageOrientation = (imageUrl, callback) => {
    const img = new Image();
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const orientation = height > width ? 'portrait' : 'landscape';
      setOriginalImageDimensions({ width, height });
      setImageOrientation(orientation);

      // If it's a callback, return the data
      if (callback) callback(orientation, width, height);
    };
    img.src = imageUrl;
  };

  // Helper function for creating the initial crop based on image dimensions
  const centerAspectCrop = (mediaWidth, mediaHeight, aspect) => {
    // If the image is landscape and we want a portrait crop
    if (mediaWidth > mediaHeight && aspect < 1) {
      // Create a square crop centered on the image if image is landscape
      return centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: (mediaHeight / mediaWidth) * 100 * aspect, // Makes the crop width match the aspect ratio
          },
          aspect,
          mediaWidth,
          mediaHeight
        ),
        mediaWidth,
        mediaHeight
      );
    }

    // If image is portrait or square, create a centered crop
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90, // Start with a crop that's 90% of the image width
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  };

  // Handle file selection
  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) {
      return;
    }

    if (!isValidImageFile(selectedFile)) {
      setError('Please upload a valid image file (JPG, JPEG, PNG).');
      return;
    }

    try {
      // Track photo upload with session tracker
      await sessionTracker.updateSessionStage('photo_uploaded');
    } catch (trackingError) {
      console.error("Session tracking error:", trackingError);
      // Continue with photo upload even if tracking fails
    }

    setFile(selectedFile);

    // Upload original image to S3 in background
    try {
      const userData = {
        name: sessionStorage.getItem('userName') || 'user',
        companyName: sessionStorage.getItem('companyName') || '',
        industry: sessionStorage.getItem('industry') || '',
        phoneNumber: sessionStorage.getItem('phoneNumber') || ''
      };
      
      // Upload original file to S3 (don't wait for it to complete)
      uploadOriginalImageToS3(selectedFile, userData, 'upload')
        .then(async result => {
          if (result.success) {
            console.log('✅ Original image uploaded to S3:', result.url);
            sessionStorage.setItem('originalImageS3Url', result.url);
            
            // Track original image S3 upload
            try {
              await sessionTracker.trackOriginalImageS3Upload({
                url: result.url,
                imageType: 'upload'
              });
            } catch (trackingError) {
              console.warn('Session tracking error for original S3 upload:', trackingError);
            }
          } else {
            console.error('❌ Original image S3 upload failed:', result.error);
          }
        })
        .catch(error => {
          console.error('❌ Original image S3 upload error:', error);
        });
    } catch (s3Error) {
      console.error('S3 upload error:', s3Error);
      // Continue with local processing even if S3 upload fails
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      setPreviewUrl(imageUrl);

      // Reset crop state
      setCrop(undefined);
      setCompletedCrop(null);
      setCroppedImageUrl(null);
      setIsCropComplete(false);

      // Show crop modal
      setShowCropModal(true);
      // Prevent body scroll when modal is open
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const imageUrl = fileReader.result;

        // Detect orientation and set up initial crop
        detectImageOrientation(imageUrl, (orientation) => {
          // Clear any previous errors
          setError(null);

          // Set file and continue with upload
          setFile(droppedFile);
          setPreviewUrl(imageUrl);

          // Reset crop state
          setCompletedCrop(null);
          setCroppedImageUrl(null);

          // Show crop modal
          setShowCropModal(true);
          setShowPreviewModal(false);
          setIsCropComplete(false);
        });
      };
      fileReader.readAsDataURL(droppedFile);
    }
  };

  // Reset crop to center when the aspect ratio changes
  useEffect(() => {
    if (imgRef.current && previewUrl) {
      const { width, height } = imgRef.current;
      const newAspect = useFixedAspectRatio ? 9 / 16 : undefined;

      if (width && height) {
        if (newAspect) {
          // Create a centered crop with the new aspect ratio
          const newCrop = centerAspectCrop(width, height, newAspect);
          setCrop(newCrop);
        } else {
          // For free-form cropping, start with a reasonably sized crop
          setCrop({
            unit: '%',
            width: 80,
            height: 80,
            x: 10,
            y: 10
          });
        }
      }
    }
  }, [useFixedAspectRatio, previewUrl]);

  // This is to initialize the crop when an image is loaded
  const onImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;

    // Calculate initial crop based on the aspect ratio
    if (aspect) {
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 80, // Start with 80% of the width
          },
          aspect,
          naturalWidth,
          naturalHeight
        ),
        naturalWidth,
        naturalHeight
      );

      setCrop(crop);

      // Generate the initial cropped image
      setCompletedCrop(crop);
      generateCroppedImage(crop);
    }
  };

  // Generate cropped image whenever crop changes
  const generateCroppedImage = (crop) => {
    if (!imgRef.current || !crop || !crop.width || !crop.height) return;

    const img = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;

    // Use the original dimensions and account for scaling
    const pixelCrop = {
      x: crop.x * scaleX,
      y: crop.y * scaleY,
      width: crop.width * scaleX,
      height: crop.height * scaleY,
    };

    // Set canvas dimensions to match the cropped area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped portion to the canvas
    ctx.drawImage(
      img,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Store crop data for proper processing later
    const newCropData = {
      originalWidth: img.naturalWidth,
      originalHeight: img.naturalHeight,
      croppedWidth: pixelCrop.width,
      croppedHeight: pixelCrop.height,
      sourceX: pixelCrop.x,
      sourceY: pixelCrop.y,
      aspectRatio: pixelCrop.width / pixelCrop.height,
      // Add percentage values for more reliable cropping
      widthPercentage: (pixelCrop.width / img.naturalWidth) * 100,
      heightPercentage: (pixelCrop.height / img.naturalHeight) * 100,
      leftPercentage: (pixelCrop.x / img.naturalWidth) * 100,
      topPercentage: (pixelCrop.y / img.naturalHeight) * 100,
      // Add flag to prevent automatic height scaling
      preserveExactCrop: true,
      // Force display mode to 'contain' to show the entire cropped area
      objectFit: "contain",
      // Set position to ensure image is visible and aligned
      objectPosition: "left bottom",
      // Add clear metadata about the crop for debugging
      cropInfo: {
        originalImageWidth: img.width,
        originalImageHeight: img.height,
        displayCropX: crop.x,
        displayCropY: crop.y,
        displayCropWidth: crop.width,
        displayCropHeight: crop.height,
        cropUnit: crop.unit || '%',
        // Add additional debug info
        pixelCropX: pixelCrop.x,
        pixelCropY: pixelCrop.y,
        pixelCropWidth: pixelCrop.width,
        pixelCropHeight: pixelCrop.height,
        imageNaturalWidth: img.naturalWidth,
        imageNaturalHeight: img.naturalHeight,
        scaleFactorX: scaleX,
        scaleFactorY: scaleY
      }
    };
    
    setCropData(newCropData);

    // Convert to high-quality JPEG blob
    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }

      // Create URL for preview
      if (croppedImageUrl) {
        URL.revokeObjectURL(croppedImageUrl);
      }
      const newCroppedUrl = URL.createObjectURL(blob);
      setCroppedImageUrl(newCroppedUrl);

      // Create a File object for the cropped image
      const croppedFile = new File([blob], file ? file.name : 'cropped-image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      // Update the file state with the cropped version
      setFile(croppedFile);
      setIsCropComplete(true);

    }, 'image/jpeg', 0.95); // High quality JPEG
  };

  // Handle crop changes
  const handleCropChange = (newCrop) => {
    setCrop(newCrop);
    // Update preview in real-time for better visual feedback
    if (newCrop.width && newCrop.height) {
      generateCroppedImage(newCrop);
    }
  };

  // Handle crop complete - when user stops dragging
  const handleCropComplete = (crop, pixelCrop) => {
    setCompletedCrop(pixelCrop);
    generateCroppedImage(pixelCrop);
  };

  // Handle completing the crop and moving to preview
  const handleCompleteCrop = () => {
    if (completedCrop) {
      // Make sure we have the final cropped image
      generateCroppedImage(completedCrop);

      // Close crop modal and show preview
      setTimeout(() => {
        setShowCropModal(false);
        setShowPreviewModal(true);
      }, 100);
    } else {
      // If no crop is completed yet, use current crop
      generateCroppedImage(crop);
      setTimeout(() => {
        setShowCropModal(false);
        setShowPreviewModal(true);
      }, 200);
    }
  };

  // Function to get cropped file for processing
  const getCroppedFile = async () => {
    // With our new approach, the main file is already updated to be the cropped version
    
    return file;
  };

  // Helper function to simulate API response for testing/demo purposes
  const simulateCharacterGeneration = async (imageFile) => {
    try {
      // Simulate processing time
      setProcessingStep('Generating your poster...');

      // Wait to simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Apply background removal to the original image instead
      const bgRemovedImageUrl = await removeImageBackground(imageFile);
      return bgRemovedImageUrl;

    } catch (error) {
      return previewUrl; // Return original image if even the simulation fails
    }
  };

  // Helper function for detailed error logging
  const logDetailedError = (error, context) => {
    console.error(`=== ERROR IN ${context} ===`);
    console.error('Error message:', error.message);
    console.error('Error name:', error.name);

    if (error.response) {
      console.error('Response status:', error.response.status);
      // Headers and response data removed to avoid exposing sensitive information
      // We don't log response data/headers to prevent exposing API keys or tokens
    }

    // Don't log request object to avoid exposing auth tokens or API keys
    
    // Don't log full error stack as it might reveal sensitive paths or data
    console.error(`=== END ERROR LOG FOR ${context} ===`);
  };

  // Add this function to simulate gradual progress during Segmind processing
  const simulateProgressDuringProcessing = (startProgress, endProgress, duration, callback) => {
    const startTime = Date.now();
    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = startProgress + ((elapsed / duration) * (endProgress - startProgress));

      if (elapsed < duration) {
        callback(Math.min(progress, endProgress));
        requestAnimationFrame(updateProgress);
      } else {
        callback(endProgress);
      }
    };

    updateProgress();
  };

  // Use Segmind Consistent Character with Pose API
  const generateConsistentCharacter = async (imageFile) => {
    try {
      // For debugging - write to console that we are starting
      

      setProcessingStep('Generating your poster...');
      setLoadingProgress(10);

      // Convert the uploaded image to base64
      const faceImageBase64 = await fileToBase64(imageFile);
      

      // Load the pose image and convert to base64
      const poseImagePath = '/images/freepik__a-confident-young-indian-woman-with-straight-black__53397 (1).jpeg';
      

      // Start simulating gradual progress from 10% to 30% while loading pose image
      simulateProgressDuringProcessing(10, 30, 3000, setLoadingProgress);

      const response = await fetch(poseImagePath);
      if (!response.ok) {
        throw new Error(`Failed to fetch pose image: ${response.status} ${response.statusText}`);
      }

      const poseImageBlob = await response.blob();
      

      const poseImageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(poseImageBlob);
      });
      

      setProcessingStep('Generating your poster...');
      setLoadingProgress(30);

      // Prepare the request to Segmind API
      const apiKey = 'SG_13f9868f102f0d83'; // Segmind API key
      

      // Check if API key is valid
      if (!apiKey || !apiKey.startsWith('SG_')) {
        console.error('Invalid Segmind API key format');
        setProcessingStep('Generating your poster...');
        return simulateCharacterGeneration(imageFile);
      }

      const requestData = {
        base_64: true,
        prompt: "A professional looking person in formal business attire, confident pose, standing straight, clear facial features, high quality portrait, studio lighting, crisp details, professional photography, marketing poster style",
        face_image: faceImageBase64,
        pose_image: poseImageBase64,
        seed: Math.floor(Math.random() * 1000000),
        samples: 1
      };

      setProcessingStep('Generating your poster...');

      // Start simulating gradual progress during the main Segmind processing
      // This will gradually increase from 30% to 65% over 30 seconds
      simulateProgressDuringProcessing(30, 65, 30000, setLoadingProgress);

      // Use Fetch API as primary method since it works better with Segmind
      
      try {
        const apiUrl = 'https://api.segmind.com/v1/consistent-character-with-pose';
        

        const fetchResponse = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        if (!fetchResponse.ok) {
          throw new Error(`Fetch API failed with status: ${fetchResponse.status}`);
        }

        const responseBlob = await fetchResponse.blob();
        

        setProcessingStep('Generating your poster...');
        setLoadingProgress(70);

        // Convert the blob response to a URL for preview if needed
        const generatedImageUrl = URL.createObjectURL(responseBlob);
        
        // Clean up the URL object to prevent memory leaks
        URL.revokeObjectURL(generatedImageUrl);

        setProcessingStep('Generating your poster...');
        setLoadingProgress(75);

        // Convert the blob to a file for background removal
        const generatedFile = new File([responseBlob], "consistent-character.jpg", { type: "image/jpeg" });

        // Remove background from the generated image
        const bgRemovedImageUrl = await removeImageBackground(generatedFile);
        

        return bgRemovedImageUrl;
      } catch (fetchError) {
        logDetailedError(fetchError, 'FETCH API CALL');
        

        try {
          // Fallback to Axios
          
          const apiUrl = 'https://api.segmind.com/v1/consistent-character-with-pose';

          const apiResponse = await axios({
            method: 'post',
            url: apiUrl,
            data: requestData,
            headers: {
              'x-api-key': apiKey,
              'Content-Type': 'application/json'
            },
            responseType: 'blob',
            timeout: 90000 // 90 second timeout (increased from 60s)
          });

          

          // Convert the blob response to a URL
          const generatedImageUrl = URL.createObjectURL(apiResponse.data);
          
          // Clean up URL object
          URL.revokeObjectURL(generatedImageUrl);

          // Convert the blob to a file for background removal
          const generatedFile = new File([apiResponse.data], "consistent-character.jpg", { type: "image/jpeg" });

          // Remove background from the generated image
          const bgRemovedImageUrl = await removeImageBackground(generatedFile);

          return bgRemovedImageUrl;
        } catch (axiosError) {
          logDetailedError(axiosError, 'AXIOS FALLBACK');
          
          return simulateCharacterGeneration(imageFile);
        }
      }
    } catch (error) {
      logDetailedError(error, 'OVERALL CHARACTER GENERATION');

      // If character generation fails, fall back to original image with background removal
      
      return removeImageBackground(imageFile);
    }
  };

  // Background removal API function
  const removeImageBackground = async (imageFile) => {
    try {
      setProcessingStep('Removing background...');
      
      

      // Use cropped image URL directly if it exists, convert to File if needed
      let fileToProcess = imageFile;
      if (croppedImageUrl && typeof croppedImageUrl === 'string' && croppedImageUrl.startsWith('blob:')) {
        try {
          
          const response = await fetch(croppedImageUrl);
          const blob = await response.blob();
          fileToProcess = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
          
        } catch (err) {
          console.error('Failed to use cropped image URL:', err);
        }
      }

      // Get API key from environment or use fallback
      const segmindApiKey = process.env.REACT_APP_SEGMIND_API_KEY || 'SG_13f9868f102f0d83';

      // Check if imageFile is valid
      if (!fileToProcess || !(fileToProcess instanceof File)) {
        console.error('Invalid image file for background removal:', fileToProcess);
        throw new Error('Invalid image file');
      }

      // Convert the file to base64 for API
      let base64Image;
      try {
        base64Image = await fileToBase64(fileToProcess);
        

        // Remove the data URI prefix for API
        base64Image = base64Image.replace(/^data:image\/\w+;base64,/, "");
      } catch (base64Error) {
        console.error('Failed to convert image to base64:', base64Error);
        throw new Error('Failed to process image');
      }

      // Try different background removal approaches
      let result = null;

      // First try: Segmind background-eraser API (newer endpoint)
      try {
        
        setProcessingStep('Removing background (method 1)...');

        const response = await fetch('https://api.segmind.com/v1/background-eraser', {
          method: 'POST',
          headers: {
            'x-api-key': segmindApiKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image: base64Image,
            return_mask: false,
            invert_mask: false,
            grow_mask: 0,
            base64: false
          })
        });

        if (!response.ok) {
          throw new Error(`Background-eraser API responded with status: ${response.status}`);
        }

        const blob = await response.blob();
        if (!blob || blob.size === 0) {
          throw new Error('Empty response from background-eraser API');
        }

        result = URL.createObjectURL(blob);
        

        // If we successfully got a result, return it
        if (result) {
        setLoadingProgress(80);
          return result;
        }
      } catch (error) {
        console.error('Background-eraser API failed:', error);
        // Continue to next method
      }

      // Second try: Original bg-removal API
      try {
        
        setProcessingStep('Removing background (method 2)...');

        const response = await fetch('https://api.segmind.com/v1/bg-removal-v2', {
          method: 'POST',
          headers: {
            'x-api-key': segmindApiKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image: base64Image,
            response_format: 'png',
            method: 'object'
          })
        });

        if (!response.ok) {
          throw new Error(`Bg-removal API responded with status: ${response.status}`);
        }

        const blob = await response.blob();
        if (!blob || blob.size === 0) {
          throw new Error('Empty response from bg-removal API');
        }

        result = URL.createObjectURL(blob);
        

        // If we successfully got a result, return it
        if (result) {
          setLoadingProgress(80);
          return result;
        }
      } catch (error) {
        console.error('Bg-removal API failed:', error);
        // Continue to next method
      }

      // Third try: FormData approach
      try {
        
        setProcessingStep('Removing background (method 3)...');

        const formData = new FormData();
        formData.append('file', fileToProcess);

        const response = await axios({
            method: 'post',
            url: 'https://api.segmind.com/v1/bg-removal',
          data: formData,
            headers: {
            'x-api-key': segmindApiKey,
            'Content-Type': 'multipart/form-data'
            },
            responseType: 'blob'
          });

        if (!response.data || response.data.size === 0) {
          throw new Error('Empty response from FormData approach');
        }

        result = URL.createObjectURL(response.data);
        

        // If we successfully got a result, return it
        if (result) {
          setLoadingProgress(80);
          return result;
        }
      } catch (error) {
        console.error('FormData approach failed:', error);
        // Fall back to original image
      }

      // If all methods failed, return original image
      
      return croppedImageUrl || previewUrl;

    } catch (error) {
      console.error('Fatal error in background removal process:', error);
      setLoadingProgress(100);
      return croppedImageUrl || previewUrl; // Return cropped image or original image as last resort
    }
  };

  // Function to enhance image using ESRGAN model from Segmind
  const enhanceImageWithESRGAN = async (imageUrl) => {
    try {
      setProcessingStep('Enhancing your selfie...');
      // Start at current progress
      const currentProgress = loadingProgress;

      // Simulate gradual progress during enhancement process
      simulateProgressDuringProcessing(currentProgress, 90, 5000, setLoadingProgress);

      // Convert the URL to a file for processing if needed
      let imageFile = imageUrl;

      // In a real implementation, we would call the Segmind ESRGAN API here
      // For now, we'll simulate the enhancement by just waiting
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Return the image URL (which would be the enhanced image URL in production)
      return imageUrl;

    } catch (error) {
      console.error('Error enhancing image:', error);
      // If enhancement fails, return the original image
      return imageUrl;
    }
  };

  // Start the processing after user confirms in the preview modal
  const startProcessing = async () => {
    if (!file) {
      setError('No image file available.');
      return;
    }

    try {
      setShowPreviewModal(false);
      setShowProcessingModal(true);
      setIsLoading(true);
      setLoadingProgress(0);

      // First, upload the cropped preview to S3
      setProcessingStep('Uploading cropped image...');
      setLoadingProgress(10);

      let processedImageUrl = croppedImageUrl || previewUrl;
      let useS3Url = false;

      if (croppedImageUrl) {
        try {
          const userData = {
            name: sessionStorage.getItem('userName') || 'user',
            companyName: sessionStorage.getItem('companyName') || '',
            industry: sessionStorage.getItem('industry') || '',
            phoneNumber: sessionStorage.getItem('phoneNumber') || '',
            isSelfieMode: isSelfieMode
          };

          // Upload cropped preview to S3
          const s3Result = await uploadCroppedPreviewToS3(croppedImageUrl, userData, cropData);
          
          if (s3Result.success) {
            console.log('✅ Cropped preview uploaded to S3:', s3Result.url);
            sessionStorage.setItem('croppedImageS3Url', s3Result.url);
            processedImageUrl = s3Result.url; // Use S3 URL for processing
            useS3Url = true;
            
            // Track cropped preview S3 upload
            try {
              await sessionTracker.trackCroppedImageS3Upload({
                url: s3Result.url
              });
            } catch (trackingError) {
              console.warn('Session tracking error for cropped S3 upload:', trackingError);
            }
          } else {
            console.error('❌ Cropped preview S3 upload failed:', s3Result.error);
            // Continue with local URL
          }
        } catch (s3Error) {
          console.error('❌ S3 upload error:', s3Error);
          // Continue with local URL
        }
      }

      // Create a proper file from the cropped image URL if we need it for local processing
      let fileToProcess = file;
      if (!useS3Url && croppedImageUrl) {
        try {
          const response = await fetch(croppedImageUrl);
          const blob = await response.blob();
          fileToProcess = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
        } catch (err) {
          console.error('Failed to use cropped image URL:', err);
        }
      }

      // Simple incremental progress - move to 20%
      setLoadingProgress(20);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Then 40%
      setLoadingProgress(40);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Then 60%
      setLoadingProgress(60);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Then 80%
      setLoadingProgress(80);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Then 90% and stay there
      setLoadingProgress(90);

      // Process the image (using S3 URL if available, otherwise local processing)
      setProcessingStep('Processing your photo...');

      try {
        if (useS3Url) {
          // Use S3 URL for processing (more reliable for background removal APIs)
          console.log('🌐 Using S3 URL for processing:', processedImageUrl);
          
          // For S3 URL processing, we need to convert it to a File-like object for the background removal
          try {
            const response = await fetch(processedImageUrl);
            const blob = await response.blob();
            const s3File = new File([blob], 'cropped-s3-image.jpg', { type: 'image/jpeg' });
            processedImageUrl = await removeImageBackground(s3File);
          } catch (error) {
            console.error('S3 URL processing failed, using original S3 URL:', error);
            // Keep the S3 URL as-is if processing fails
          }
        } else {
          // Use local file processing
          console.log('💻 Using local file for processing');
          processedImageUrl = await removeImageBackground(fileToProcess);
        }

        // Apply enhancement
        setProcessingStep('Enhancing your photo...');
        processedImageUrl = await enhanceImageWithESRGAN(processedImageUrl);

      } catch (error) {
        console.error('Photo processing failed:', error);
        logDetailedError(error, 'PHOTO_PROCESSING');
        // Use the best available URL (S3 if uploaded, otherwise local)
        processedImageUrl = useS3Url ? sessionStorage.getItem('croppedImageS3Url') : (croppedImageUrl || previewUrl);
      }

      // Store the processed image
      setProcessedImage(processedImageUrl);

      // Only reach 100% right before transitioning
      setLoadingProgress(100);
      await new Promise(resolve => setTimeout(resolve, 1200));

      // Handle successful processing
      handleContinue(processedImageUrl);

    } catch (error) {
      console.error('Error processing image:', error);
      logDetailedError(error, 'OVERALL_PROCESSING');
      setError('Failed to process image. Please try again.');
      setShowProcessingModal(false);
      setIsLoading(false);
    }
  };

  // Submit the user data and proceed to next step
  const handleContinue = async (finalImage) => {
    try {
      // Track progression to poster generation
      await sessionTracker.updateSessionStage('photo_processed');
    } catch (trackingError) {
      console.error("Session tracking error:", trackingError);
      // Continue with navigation even if tracking fails
    }

    try {
      
      // Get user data from previous steps or use default values
      const industry = sessionStorage.getItem('industry') || 'other';

      // Make sure cropData is set with additional positioning information for proper display
      const currentCropData = cropData || {
        originalWidth: imgRef.current?.naturalWidth || 0,
        originalHeight: imgRef.current?.naturalHeight || 0,
        croppedWidth: completedCrop?.width || 0,
        croppedHeight: completedCrop?.height || 0,
        aspectRatio: completedCrop ? completedCrop.width / completedCrop.height : aspect,
        // Add positioning data to ensure image sits at the bottom of the container
        positionFromBottom: true,
        alignToBottom: true,
        // Add fit settings to ensure proper rendering in the poster
        objectFit: "cover",
        objectPosition: "center bottom"
      };

      const userData = {
        name: sessionStorage.getItem('userName') || 'Your Name',
        companyName: sessionStorage.getItem('companyName') || '', // Don't default to 'Your Company' - preserve empty if no company
        industry: industry, // Pass industry directly
        tagline: sessionStorage.getItem('tagline') || 'Your Tagline',
        phoneNumber: sessionStorage.getItem('phoneNumber') || 'Your Phone',
        isNormalSelfie: isNormalSelfie, // Add the flag to indicate if this is a normal selfie
        isSelfieMode: isSelfieMode, // Add the flag to indicate if this is a selfie mode
        cropData: currentCropData // Store crop data for proper image processing on poster
      }

      // Log industry value for debugging
      
      
      

      // Make sure we have the final image URL (either processed or cropped)
      const finalImageUrl = finalImage || processedImage || croppedImageUrl || previewUrl;
      

      // Store data in session storage using string format to ensure proper persistence
      sessionStorage.setItem('userData', JSON.stringify(userData));

      // Store the image URL directly without any conversion
      sessionStorage.setItem('processedImage', finalImageUrl);

      // Store crop data separately for easier access
      sessionStorage.setItem('cropData', JSON.stringify(currentCropData));
      

      // Force scroll to top before navigation
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;

      // Navigate to poster creation page
      navigate('/share-poster');

      // Also attempt to scroll after navigation with a slight delay
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }, 50);
    } catch (error) {
      setError('Failed to submit data. Please try again.');
      setShowProcessingModal(false);
      setIsLoading(false);
    }
  };

  // Add custom CSS to improve the ReactCrop styling
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      /* General UI styles */
      .forced-small-logo {
        max-width: 230px !important;
        width: 230px !important;
      }

      .logo-container-fix {
        top: 45px !important;
      }

      /* Portrait note styling */
      .portrait-note {
        color: #0083B5;
        font-size: 13px;
        margin: 5px 0;
        font-weight: 500;
      }

      .portrait-note i {
        margin-right: 5px;
      }

      /* Make ReactCrop more intuitive */
      .ReactCrop {
        position: relative;
        max-height: 65vh;
        max-width: 100%;
        border: 2px solid #0083B5;
        border-radius: 8px;
        overflow: hidden;
        background-color: #f0f0f0;
      }

      /* Desktop-specific crop container size */
      @media (min-width: 768px) {
        .flexible-cropper-layout,
        .reactcrop-container,
        .reactcrop-wrapper,
        .ReactCrop {
          max-width: 450px !important;
          max-height: 450px !important;
          margin: 0 auto !important;
        }

        .ReactCrop img {
          max-width: 100% !important;
          max-height: 450px !important;
          object-fit: contain !important;
        }
      }

      .ReactCrop:hover {
        box-shadow: 0 0 15px rgba(0, 131, 181, 0.5);
      }

      .ReactCrop__crop-selection {
        border: 3px solid #00a0ff !important;
        box-shadow: 0 0 0 9999em rgba(0, 0, 0, 0.7) !important;
      }

      .ReactCrop__handle {
        width: 24px !important;
        height: 24px !important;
        background-color: #00a0ff !important;
        border: 3px solid white !important;
        opacity: 0.95 !important;
      }

      .ReactCrop__handle:hover {
        transform: scale(1.2) !important;
      }

      .ReactCrop__drag-handle {
        width: 40px !important;
        height: 40px !important;
      }

      /* Improved preview layout for desktop */
      @media (min-width: 768px) {
        .crop-modal-content {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
        }

        .crop-area-container {
          flex: 1;
          max-width: 65%;
        }

        .crop-preview-container {
          flex: 1;
          max-width: 35%;
          position: sticky;
          top: 20px;
        }
      }

      /* Mobile layout adjustments */
      @media (max-width: 767px) {
        .crop-modal-content {
          display: flex;
          flex-direction: column;
        }

        .crop-area-container {
          width: 100%;
          margin-bottom: 15px;
        }

        .crop-preview-container {
          width: 100%;
        }
      }

      /* More prominent preview styles */
      .crop-preview {
        background-color: #f8f9fa;
        padding: 15px;
        border-radius: 8px;
        text-align: center;
        border: 1px solid #e0e0e0;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }

      .preview-title {
        font-size: 16px;
        margin: 0 0 15px 0;
        color: #0083B5;
        font-weight: bold;
        position: relative;
      }

      .preview-title:after {
        content: '';
        position: absolute;
        bottom: -5px;
        left: 50%;
        transform: translateX(-50%);
        width: 50px;
        height: 2px;
        background-color: #0083B5;
      }

      .preview-image-container {
        max-width: 100%;
        margin: 0 auto;
        border: 2px solid #0083B5;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      }

      .preview-image {
        width: 100%;
        height: auto;
        display: block;
      }

      .crop-instruction-text {
        background-color: rgba(0, 131, 181, 0.1);
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 15px;
        width: 100%;
        text-align: center;
        font-weight: 500;
        border-left: 4px solid #0083B5;
        color: #333;
      }

      /* Preview modal styles */
      .preview-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.7);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        overflow: auto;
        padding: 20px;
      }

      .preview-modal {
        background-color: white;
        border-radius: 8px;
        width: 90%;
        height: auto;
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        z-index: 1001;
      }

      .preview-modal-header {
        padding: 15px;
        border-bottom: 1px solid #eee;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .preview-modal-header h2 {
        margin: 0;
        font-size: 18px;
        color: #0083B5;
      }

      .modal-close-btn {
        background: none;
        border: none;
        font-size: 18px;
        cursor: pointer;
        color: #666;
      }

      .preview-modal-body {
        max-height: 70vh;
        overflow-y: auto;
        padding: 15px;
      }

      .preview-modal-footer {
        padding: 15px;
        border-top: 1px solid #eee;
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .preview-modal-buttons {
        display: flex;
        justify-content: center;
        gap: 15px;
        width: 100%;
      }

      .retake-btn, .confirm-btn {
        padding: 12px 24px;
        border-radius: 4px;
        border: none;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 160px;
        justify-content: center;
        transition: all 0.2s ease;
      }

      .retake-btn {
        background-color: #f5f5f5;
        color: #333;
      }

      .retake-btn:hover {
        background-color: #e5e5e5;
      }

      .confirm-btn {
        background-color: #0083B5;
        color: white;
      }

      .confirm-btn:hover {
        background-color: #006d99;
      }

      .confirm-btn:disabled {
        background-color: #cccccc;
        color: #666666;
        cursor: not-allowed;
      }

      /* Silhouette guide styles */
      .person-outline-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        pointer-events: none;
        z-index: 10;
      }

      .face-outline {
        width: 95%;
        max-width: 380px;
        height: auto;
        max-height: 80vh; /* Ensure it fits within the viewport height */
        opacity: 0.9;
        filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.5));
        object-fit: contain; /* Maintain aspect ratio */
        margin-top: -5vh; /* Move slightly upward to better center in the camera view */
      }

      .camera-instructions {
        color: white;
        text-shadow: 0 0 4px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.6);
        font-size: 16px;
        font-weight: 500;
        text-align: center;
        background-color: rgba(0, 0, 0, 0.4);
        padding: 8px 12px;
        border-radius: 20px;
        margin-top: 10px;
        max-width: 80%;
      }

      /* Camera modal adjustments for the new silhouette */
      .camera-modal {
        background-color: rgba(0, 0, 0, 0.9);
      }

      .camera-content {
        background-color: rgba(0, 0, 0, 0.85);
        color: white;
        width: 95%;
        max-width: 500px;
        height: 90vh;
        max-height: 800px;
        display: flex;
        flex-direction: column;
      }

      .camera-container {
        position: relative;
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        width: 100%;
        height: calc(100% - 60px);
        background-color: #000;
      }

      .camera-video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .camera-btn {
        position: absolute;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: none;
        background-color: white;
        font-size: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
      }

      .camera-btn i {
        font-size: 30px;
        color: #0083B5;
      }

      .camera-btn:hover {
        background-color: #0083B5;
      }

      .camera-btn:hover i {
        color: white;
      }

      .camera-mode-btn {
        position: absolute;
        bottom: 30px;
        right: 20px;
        background-color: rgba(0, 0, 0, 0.7);
        color: white;
        border: none;
        border-radius: 20px;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 14px;
        cursor: pointer;
        z-index: 20;
      }

      .camera-mode-btn:hover {
        background-color: rgba(0, 0, 0, 0.9);
      }

      /* Mobile layout adjustments */
      @media (max-width: 767px) {
        .camera-content {
          width: 100%;
          height: 100vh;
          border-radius: 0;
        }

        .face-outline {
          width: 85%;
          max-width: 250px;
        }

        .camera-instructions {
          font-size: 14px;
          padding: 6px 10px;
        }

        .camera-btn {
          bottom: 20px;
          width: 60px;
          height: 60px;
        }

        .camera-btn i {
          font-size: 24px;
        }

        .camera-mode-btn {
          bottom: 20px;
          font-size: 12px;
          padding: 6px 10px;
        }
      }

      /* Modal header styles */
      .modal-header {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        padding: 12px 15px !important;
        background-color: #0083B5 !important;
        color: white !important;
        border-top-left-radius: 8px !important;
        border-top-right-radius: 8px !important;
        z-index: 20 !important;
        position: relative !important;
        flex-wrap: wrap !important;
      }

      .modal-header h3 {
        margin: 0 !important;
        font-size: 18px !important;
        font-weight: 500 !important;
        margin-right: auto !important;
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

    // Generate initial crop
    setTimeout(() => {
      generateCroppedImage();
    }, 100);

  // Add missing handleDragOver function
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Activate camera for photo capture
  const activateCamera = async (selfieMode) => {
    setIsSelfieMode(selfieMode);
    setShowCameraModal(true);
    // Prevent body scroll when modal is open
    document.body.classList.add('modal-open');
    document.documentElement.classList.add('modal-open');
    

    try {
      // Stop any existing stream first
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
      }

      // Request camera access with appropriate settings
      const constraints = {
        video: {
          facingMode: selfieMode ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 0.75 }, // 3:4 portrait aspect ratio
        }
      };

      

      // Get media stream
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Attach to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(err => {
            console.error('Error playing video:', err);
          });
          setIsCameraActive(true);
          
        };
      }
    } catch (err) {
      setError(`Camera access error: ${err.message || 'Permission denied or camera not available'}`);
      setShowCameraModal(false);
      setIsCameraActive(false);
      // Re-enable body scroll if camera fails
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    }
  };

  // Take photo from the camera
  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    try {
      // Track photo capture with session tracker
      await sessionTracker.updateSessionStage('photo_captured');
    } catch (trackingError) {
      console.error("Session tracking error:", trackingError);
      // Continue with photo capture even if tracking fails
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Use a consistent portrait aspect ratio for both modes
    const aspectRatio = 3/4; // Portrait aspect ratio (width/height)

    // Get video dimensions
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    

    // Calculate canvas dimensions to maintain portrait orientation
    let canvasWidth, canvasHeight;

    // For selfie mode (front camera)
    if (isSelfieMode) {
      if (videoWidth > videoHeight) {
        // Landscape video (typical for front cameras)
        canvasHeight = Math.min(videoHeight, 1280);
      canvasWidth = canvasHeight * aspectRatio;
      } else {
        // Already portrait
        canvasWidth = Math.min(videoWidth, 960);
        canvasHeight = canvasWidth / aspectRatio;
      }
    } else {
      // For back camera
      if (videoWidth > videoHeight) {
        // Landscape video
        canvasHeight = videoHeight;
        canvasWidth = canvasHeight * aspectRatio;
      } else {
        // Already portrait
        canvasWidth = videoWidth;
        canvasHeight = canvasWidth / aspectRatio;
      }
    }

    // Set canvas dimensions
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Get canvas context
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Clear previous contents
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate centering offsets
    const offsetX = (videoWidth - canvasWidth) / 2;

    // Adjust vertical offset based on device and mode
    let offsetY;

    // For selfie mode, we need to center the face better
    if (isSelfieMode) {
      // Center vertically for selfie (less offset)
      offsetY = (videoHeight - canvasHeight) / 2 - (window.innerWidth <= 768 ? canvasHeight * 0.05 : 0);
    } else {
      // For back camera, center vertically
      offsetY = (videoHeight - canvasHeight) / 2;
    }

    

    // For selfie mode, flip the image horizontally
    if (isSelfieMode) {
      // Save context state
      ctx.save();

      // Translate and scale to flip horizontally
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);

      // Draw the flipped image
      ctx.drawImage(
        video,
        offsetX, offsetY, canvasWidth, canvasHeight, // Source dimensions
        0, 0, canvas.width, canvas.height // Destination dimensions
      );

      // Restore context
      ctx.restore();
    } else {
      // Regular drawing for back camera
      ctx.drawImage(
        video,
        offsetX, offsetY, canvasWidth, canvasHeight, // Source dimensions
        0, 0, canvas.width, canvas.height // Destination dimensions
      );
    }

    // Add flash effect
    const flashElement = document.createElement('div');
    flashElement.style.position = 'fixed';
    flashElement.style.top = '0';
    flashElement.style.left = '0';
    flashElement.style.width = '100%';
    flashElement.style.height = '100%';
    flashElement.style.backgroundColor = 'white';
    flashElement.style.opacity = '0.6';
    flashElement.style.zIndex = '10000';
    flashElement.style.pointerEvents = 'none';
    flashElement.style.transition = 'opacity 0.3s ease-out';
    document.body.appendChild(flashElement);

    // Play camera sound if available
    try {
      const shutterSound = new Audio('/sounds/camera-shutter.mp3');
      shutterSound.play().catch(e => console.error('Could not play shutter sound:', e));
    } catch (e) {
      
    }

    // Fade out flash effect
    setTimeout(() => {
      flashElement.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(flashElement);
      }, 300);
    }, 50);

    // Convert canvas to data URL
    const photoDataUrl = canvas.toDataURL('image/jpeg', 0.95);

    // Process the captured image
    detectImageOrientation(photoDataUrl, (orientation) => {
      // Clear any previous errors
      setError(null);

      // Set the photo as the preview
      setPreviewUrl(photoDataUrl);

      // Create a file from the data URL for processing
      const fileName = isSelfieMode ? "selfie-photo.jpg" : "camera-photo.jpg";

      // Use toBlob with proper MIME type and quality
      canvas.toBlob((blob) => {
        // Create a proper File object with explicit type
        const newFile = new File([blob], fileName, {
          type: "image/jpeg",
          lastModified: Date.now()
        });

        // Set the file for processing
        setFile(newFile);

        // Upload original camera image to S3 in background
        try {
          const userData = {
            name: sessionStorage.getItem('userName') || 'user',
            companyName: sessionStorage.getItem('companyName') || '',
            industry: sessionStorage.getItem('industry') || '',
            phoneNumber: sessionStorage.getItem('phoneNumber') || ''
          };
          
          const imageType = isSelfieMode ? 'selfie' : 'camera';
          
          // Upload original camera file to S3 (don't wait for it to complete)
          uploadOriginalImageToS3(newFile, userData, imageType)
            .then(async result => {
              if (result.success) {
                console.log('✅ Original camera image uploaded to S3:', result.url);
                sessionStorage.setItem('originalImageS3Url', result.url);
                
                // Track original camera image S3 upload
                try {
                  await sessionTracker.trackOriginalImageS3Upload({
                    url: result.url,
                    imageType: imageType
                  });
                } catch (trackingError) {
                  console.warn('Session tracking error for camera S3 upload:', trackingError);
                }
              } else {
                console.error('❌ Original camera image S3 upload failed:', result.error);
              }
            })
            .catch(error => {
              console.error('❌ Original camera image S3 upload error:', error);
            });
        } catch (s3Error) {
          console.error('Camera S3 upload error:', s3Error);
          // Continue with local processing even if S3 upload fails
        }

        // Reset crop state and show crop modal
        setCompletedCrop(null);
        setCroppedImageUrl(null);

        // Close camera modal
        stopCamera();

        // Show crop modal with slight delay to ensure smooth transition
        setTimeout(() => {
          setShowCropModal(true);
          setShowPreviewModal(false);
          setIsCropComplete(false);
          // Prevent body scroll when modal is open
          document.body.classList.add('modal-open');
          document.documentElement.classList.add('modal-open');
        }, 300);
      }, 'image/jpeg', 0.95);
    });
  };

  // Stop camera stream and clean up resources
  const stopCamera = () => {
    

    // Stop all camera tracks
    if (videoRef.current && videoRef.current.srcObject) {
      try {
      const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => {
          track.stop();
          
        });
      videoRef.current.srcObject = null;
      } catch (err) {
        console.error('Error stopping camera tracks:', err);
      }
    }

    // Reset camera state
    setIsCameraActive(false);
    setShowCameraModal(false);
    // Re-enable body scroll
    document.body.classList.remove('modal-open');
    document.documentElement.classList.remove('modal-open');

    // Clear canvas if needed
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }

    
  };

  // Clean up resources when component unmounts
  useEffect(() => {
    return () => {
      stopCamera();
      // Cleanup body scroll class when component unmounts
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    };
  }, []);

  // Convert image to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Extract the base64 string from the Data URL
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle applying the crop
  const handleApplyCrop = () => {
    if (!croppedImageUrl) return;

    // Store crop data in session storage for use in the poster generation
    if (cropData) {
      sessionStorage.setItem('cropData', JSON.stringify(cropData));
      
    }

    // Close crop modal and show preview modal
    setShowCropModal(false);
    setShowPreviewModal(true);
    // Keep modal-open class since we're switching to preview modal
  };

  // Handle choosing another image
  const handleChooseAnotherImage = () => {
    setShowCropModal(false);
    setImageSrc(null);
    setFile(null);
    setCroppedImageUrl(null);
    setCropData(null);
    setIsCropComplete(false);
    setCrop(undefined);
    setCompletedCrop(null);
    // Re-enable body scroll
    document.body.classList.remove('modal-open');
    document.documentElement.classList.remove('modal-open');

    // Clear the file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Add missing refs and state variables
  const fileInputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);

  // Check if file is a valid image
  const isValidImageFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    return validTypes.includes(file.type);
  };

  // Process uploaded file (simplified version to transition to new implementation)
  const processUploadedFile = (fileToProcess) => {
    // For the new implementation, we're directly using the cropped image
    // This is just a fallback in case cropping fails
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target.result;

      // Store the original image URL in session storage
      sessionStorage.setItem('processedImage', imageUrl);

      // Call handleContinue with the image URL
      handleContinue(imageUrl);
    };
    reader.readAsDataURL(fileToProcess);
  };

  const [showDosModal, setShowDosModal] = useState(false);

  // Add a flag to check if this is the first visit
  const isFirstVisit = useRef(true);

  // Show the DOS modal when the component mounts, but only on first visit and only on mobile
  useEffect(() => {
    if (isFirstVisit.current && isMobile) {
      setShowDosModal(true);
      // Disable body scroll when automatically opening modal
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
      isFirstVisit.current = false;
    }
  }, [isMobile]);

  // Add a function to toggle the DOS modal with body scroll management
  const toggleDosModal = () => {
    if (!showDosModal) {
      // Opening modal - disable body scroll
      document.body.classList.add('modal-open');
      document.documentElement.classList.add('modal-open');
    } else {
      // Closing modal - re-enable body scroll
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    }
    setShowDosModal(!showDosModal);
  };

  // Cleanup DOS modal body scroll on component unmount
  useEffect(() => {
    return () => {
      // Ensure modal classes are removed on component unmount
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    };
  }, []);

  // Add a style tag to forcefully override logo size and add crop styles
  useEffect(() => {
    // Create style element for general styles
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .forced-small-logo {
        max-width: 230px !important;
        width: 230px !important;
      }

      .logo-container-fix {
        top: 45px !important;
      }

      /* Custom crop styles */
      .crop-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 10px;
        width: 100%;
        max-width: 100%;
      }

      .crop-instructions {
        background-color: rgba(0, 131, 181, 0.1);
        border-radius: 8px;
        padding: 10px;
        margin-bottom: 15px;
        width: 100%;
        text-align: center;
      }

      /* Camera container styles - FIXED */
      .camera-container {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        overflow: hidden;
        background: #000;
        border-radius: 8px;
      }

      .mobile-camera-container {
        position: relative;
        width: 100%;
        height: 80vh;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        overflow: hidden;
        background: #000;
        border-radius: 8px;
      }

      .camera-video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .person-outline-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        pointer-events: none;
        z-index: 10;
      }

      .outline-image {
        width: 70%;
        max-width: 350px;
        opacity: 0.7;
      }

      .positioning-guide {
        margin-top: 20px;
        color: white;
        background-color: rgba(0, 0, 0, 0.6);
        padding: 10px 15px;
        border-radius: 20px;
        font-size: 14px;
        text-align: center;
        max-width: 90%;
      }

      .person-outline-overlay.selfie-mode .outline-image {
        width: 60%;
      }

      .person-outline-overlay.face-detected .outline-image {
        opacity: 0.8;
        border: 2px solid yellow;
      }

      .person-outline-overlay.face-in-position .outline-image {
        opacity: 1;
        border: 3px solid #4CAF50;
      }

      .camera-controls {
        position: absolute;
        bottom: 30px;
        left: 0;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 20px;
        z-index: 11;
      }

      .camera-btn {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: none;
        background-color: white;
        font-size: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
      }

      .camera-btn.active {
        background-color: #4CAF50;
        color: white;
      }

      .camera-btn.disabled {
        background-color: #ccc;
        color: #666;
        cursor: not-allowed;
      }

      .camera-btn.cancel {
        background-color: #f44336;
        color: white;
        width: 40px;
        height: 40px;
        font-size: 18px;
      }

      .crop-instruction-text {
        color: #0083B5;
        font-weight: bold;
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  return (
    <div className="upload-page">
      {/* Camera Modal */}
      {showCameraModal && (
        <div className="modal-overlay">
          <div className={`modal camera-modal ${isSelfieMode ? 'selfie-mode' : ''}`}>
            <div className="modal-header">
              <h3>{isSelfieMode ? 'Take a Selfie' : 'Take a Photo'}</h3>
              <span style={{
                color: 'white',
                fontSize: '12px',
                fontWeight: 'normal',
                marginLeft: '0px',
                marginRight: 'auto',
                display: 'block',
                width: '100%',
                opacity: '0.9'
              }}>
                {isSelfieMode
                  ? 'Position yourself within the outline'
                  : 'Center your subject within the outline'
                }
              </span>
              <button className="close-btn" onClick={stopCamera}>
                &times;
              </button>
            </div>
            <div
              className="camera-container"
              style={{
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: 0,
                padding: 0,
                aspectRatio: '3/4',
                minHeight: window.innerWidth <= 768 ? '45vh' : '60vh', // Reduced for mobile
                maxHeight: window.innerWidth <= 768 ? '60vh' : '80vh', // Reduced for mobile
                backgroundColor: '#000'
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-video"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#111',
                  ...(isSelfieMode ? { transform: 'scaleX(-1)' } : {})
                }}
                onLoadedMetadata={() => {
                  
                }}
              ></video>

              {/* Upper body outline guide - with absolute positioning */}
              <div
                className="person-outline-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                  zIndex: 5,
                  pointerEvents: 'none'
                }}
              >
                <div
                  className="outline-content-wrapper"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: '360px',
                    margin: '0 auto',
                    padding: 0,
                    textAlign: 'center',
                    position: 'relative',
                    transform: isSelfieMode ? 'translateY(-5%)' : 'translateY(0)'
                  }}
                >
                  <img
                    src="/images/outlines/upper-body-outline.svg"
                    alt="Position guide"
                    className="face-outline"
                    style={{
                      display: 'block',
                      width: window.innerWidth <= 768 ? '300px' : '340px',
                      maxWidth: '95%',
                      height: 'auto',
                      margin: '0 auto',
                      padding: 0
                    }}
                  />
                </div>
              </div>

              {/* Camera controls - with absolute positioning */}
              <div
                className="camera-controls"
                style={{
                  position: 'absolute',
                  bottom: '15px',
                  left: 0,
                  right: 0,
                  width: '100%',
                  height: '80px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                  zIndex: 10
                }}
              >
                {/* Camera button only */}
                <button
                  onClick={takePhoto}
                  className="camera-btn"
                  title="Take Photo"
                  style={{
                    width: '70px',
                    height: '70px',
                    margin: 0,
                    padding: 0,
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    border: '3px solid #0083B5',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <i
                    className="fas fa-camera"
                    style={{
                      margin: 0,
                      padding: 0,
                      fontSize: '30px',
                      color: '#0083B5'
                    }}
                  ></i>
                </button>
              </div>

              <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
            </div>
          </div>
        </div>
      )}

      {/* New DOS Modal */}
      {showDosModal && (
        <div className="dos-modal-overlay">
          <div className="dos-modal">
            <div className="dos-modal-header">
              <h2>Photo Guidelines</h2>
              <button className="dos-modal-close" onClick={toggleDosModal}>×</button>
            </div>
            <div className="dos-modal-body">
              <div className="dos-modal-image-container">
                <img src="/images/uploadphoto/dos1.png" alt="Do's 1" className="dos-modal-image" />
                <img src="/images/uploadphoto/Dos2.png" alt="Do's 2" className="dos-modal-image" />
              </div>
            </div>
            <div className="dos-modal-footer">
              <button className="understood-btn" onClick={toggleDosModal}>Understood</button>
            </div>
          </div>
        </div>
      )}

      <div className="left-section">
        {!isMobile ? (
          // Desktop view
          <>
            {/* Background image */}
            <img
              src="/images/uploadphoto/uploadphotobg.png"
              alt="Background"
              className="left-section-background"
            />

            {/* Logo at the top left */}
            <div className="left-logo-container">
              <Link to="/">
                <img
                  src="/images/LOGO.png"
                  alt="Logo"
                  className="left-logo-image forced-small-logo"
                  style={{ width: '230px' }}
                />
              </Link>
            </div>

            {/* Bumrah and You image below logo, center-aligned and positioned */}
            <div className="left-bumrah-container">
              <img
                src="/images/uploadphoto/bumraahandu.png"
                alt="Bumrah and You"
                className="left-bumrah-image"
                style={{ maxWidth: '350px' }}
              />
            </div>

            {/* Two DOS images side by side below Bumrah+YOU */}
            <div className="left-dos-container">
              <img
                src="/images/uploadphoto/dos1.png"
                alt="Do's 1"
                className="left-dos-image"
              />
              <img
                src="/images/uploadphoto/Dos2.png"
                alt="Do's 2"
                className="left-dos-image"
              />
            </div>
          </>
        ) : (
          // Mobile view - restructured as a single vertical column with proper spacing
          <>
            {/* Background image */}
            <img
              src="/images/uploadphoto/uploadphotobg.png"
              alt="Background"
              className="left-section-background"
            />

            {/* Logo at the top */}
            <div className="left-logo-container">
              <Link to="/">
                <img
                  src="/images/LOGO.png"
                  alt="Logo"
                  className="left-logo-image"
                />
              </Link>
            </div>

            {/* Bumrah and You image - 20px below logo */}
            <div className="left-bumrah-container">
              <img
                src="/images/uploadphoto/bumraahandu.png"
                alt="Bumrah and You"
                className="left-bumrah-image"
              />
            </div>

            {/* SWAPPED: Form container comes before DOS2 image */}
            <div className="form-container">
              {/* Mobile stepper inside form - using the exact same structure as AddDetails.js */}
              <div className="mobile-stepper-container form-stepper">
                <div className="mobile-stepper">
                  <div className="progress-step completed">
                    <div className="step-circle">1</div>
                    <div className="step-label">OTP</div>
                  </div>
                  <div className="progress-line active"></div>
                  <div className="progress-step completed">
                    <div className="step-circle">2</div>
                    <div className="step-label">Add Details</div>
                  </div>
                  <div className="progress-line active"></div>
                  <div className="progress-step active">
                    <div className="step-circle">3</div>
                    <div className="step-label">Upload</div>
                  </div>
                </div>
              </div>

              {/* Title after stepper indicator */}
              <h2 className="form-title">Upload Your Photo</h2>

              {/* Camera moved to modal, only showing the regular upload interface */}
              <div className="upload-area" onDragOver={handleDragOver} onDrop={handleDrop}>
                <p>Drag your file to start uploading</p>
                <div className="upload-divider"><span>or</span></div>

                <div className="upload-buttons">
                  <button
                    className="upload-btn blue-btn"
                    onClick={() => activateCamera(false)}
                  >
                    <i className="fa fa-camera"></i> Take a photo
                  </button>

                  <button
                    className="upload-btn orange-btn"
                    onClick={() => activateCamera(true)}
                  >
                    <i className="fa fa-user"></i> Take a selfie
                  </button>

                  <button
                    className="browse-button"
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <i className="fa fa-folder-open"></i> Browse for photo
                  </button>
                  <input
                    type="file"
                    id="file-input"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              {/* View Guidelines Button */}
              <button
                onClick={toggleDosModal}
                style={{
                  marginTop: '15px',
                  padding: '10px 15px',
                  backgroundColor: '#0083B5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <i className="fa fa-info-circle"></i> View Photo Guidelines
              </button>
            </div>

            {/* DOS2 image now after form container */}
            <div className="left-dos-container">
              <img
                src="/images/uploadphoto/Dos2.png"
                alt="Do's 2"
                className="left-dos-image"
              />
            </div>

            {/* NEW: Added DOS1 image after DOS2 */}
            <div className="left-dos1-container">
              <img
                src="/images/uploadphoto/dos1.png"
                alt="Do's 1"
                className="left-dos-image"
              />
            </div>
          </>
        )}
      </div>

      {/* Desktop-only right section */}
      {!isMobile && (
        <div className="right-section">
          {/* Fixed position stepper in right column */}
          <div className="fixed-stepper-container">
            <div className="fixed-stepper">
              <div className="progress-step completed">
                <div className="step-circle">1</div>
                <div className="step-label">OTP</div>
              </div>
              <div className="progress-line active"></div>
              <div className="progress-step completed">
                <div className="step-circle">2</div>
                <div className="step-label">Add Details</div>
              </div>
              <div className="progress-line active"></div>
              <div className="progress-step active">
                <div className="step-circle">3</div>
                <div className="step-label">Upload</div>
              </div>
            </div>
          </div>

          <div className="right-content">
            <div className="upload-container">
              <h1 className="upload-title">Upload Your Photo</h1>

              {error && <div className="error-message">{error}</div>}

              <div className="upload-area-container">
                {/* Camera moved to modal, only showing the regular upload interface */}
                <div
                  className="upload-area"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  {isLoading && !showProcessingModal ? (
                    <div className="loading-container">
                      <Loader />
                    </div>
                  ) : previewUrl && !showPreviewModal ? (
                    <div className="preview-container">
                      <img
                        src={croppedImageUrl}
                        alt="Preview"
                        className="preview-image"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          objectPosition: "center center",
                          backgroundColor: "#fff"
                        }}
                      />
                      <button
                        className="remove-image-btn"
                        onClick={() => {
                          setFile(null);
                          setPreviewUrl(null);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="drag-text">Drag your file to start uploading</p>
                      <div className="upload-divider">
                        <span>or</span>
                      </div>
                      <div className="upload-options">
                        <button
                          className="take-photo-btn"
                          onClick={() => {
                            setIsNormalSelfie(true);
                            activateCamera(false); // Use back camera
                          }}
                        >
                          <i className="fas fa-camera"></i> Take a photo
                        </button>
                        <button
                          className="take-photo-btn selfie-btn"
                          onClick={() => {
                            setIsNormalSelfie(true);
                            activateCamera(true); // Use front camera (selfie mode)
                          }}
                        >
                          <i className="fas fa-user"></i> Take a selfie
                        </button>
                        <label htmlFor="normal-file-upload" className="browse-btn">
                          <i className="fas fa-folder-open"></i> Browse for photo
                        </label>
                        <input
                          id="normal-file-upload"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            setIsNormalSelfie(true);
                            handleFileChange(e);
                          }}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cropping Modal */}
      {showCropModal && (
        <div className="modal-overlay" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 0
        }}>
          <div className="crop-modal" style={{
            width: '95%',
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            <div className="crop-modal-header">
              <h2>Crop Your Image</h2>
              <div className="crop-options">
                {/* Close button for both mobile and desktop */}
                <button className="modal-close-btn" onClick={() => {
                  setShowCropModal(false);
                  setImageSrc(null);
                  setFile(null);
                  setCroppedImageUrl(null);
                  setCropData(null);
                  setIsCropComplete(false);
                  setCrop(undefined);
                  setCompletedCrop(null);

                  // Clear the file input to allow selecting the same file again
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div className="crop-modal-content">
              <div className="crop-instructions-container centered-instructions">
                <p className="crop-instructions">
                  <i className="fas fa-crop-alt"></i>Drag corners to adjust crop
                </p>
                <p className="crop-instructions" style={{ marginTop: "8px", color: "#0083B5" }}>
                  <i className="fas fa-info-circle"></i>Preview is how your image will appear on the poster, so position your head below the top crop boundary for perfect poster.
                </p>
              </div>

              <div className="flexible-cropper-layout" style={{ maxWidth: "450px", maxHeight: "450px", margin: "0 auto" }}>
                <div className="reactcrop-container" style={{ maxWidth: "450px", maxHeight: "450px", margin: "0 auto" }}>
                  {/* Mobile-specific instruction - always visible */}
                  <div className="mobile-crop-instructions" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '8px',
                    backgroundColor: 'rgba(0, 131, 181, 0.1)',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    color: '#0083B5',
                    fontWeight: '500'
                  }}>
                    <i className="fas fa-crop-alt" style={{ marginRight: '5px' }}></i>
                    Drag corners to adjust crop
                  </div>
                  <div className="reactcrop-wrapper" style={{ maxWidth: "450px", maxHeight: "450px" }}>
                    <ReactCrop
                      crop={crop}
                      aspect={useFixedAspectRatio ? aspect : undefined}
                      onChange={(newCrop) => setCrop(newCrop)}
                      onComplete={(c) => {
                        setCompletedCrop(c);
                        generateCroppedImage(c);
                      }}
                      ruleOfThirds={true}
                      circularCrop={false}
                      minWidth={50}
                      minHeight={50}
                      keepSelection={true}
                    >
                      <img
                        ref={imgRef}
                        src={previewUrl}
                        onLoad={onImageLoad}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "450px",
                          margin: "0 auto",
                          display: "block",
                          objectFit: "contain"
                        }}
                        alt="Upload"
                      />
                    </ReactCrop>
                  </div>
                </div>

                {/* Preview container - hidden on mobile */}
                <div className="preview-container desktop-only">
                  <h3 className="preview-header">Preview</h3>
                  <div className={`preview-image-container ${isCropComplete ? 'crop-complete' : ''}`}>
                    {croppedImageUrl ? (
                      <img
                        src={croppedImageUrl}
                        alt="Preview"
                        className="preview-image"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          objectPosition: "center center",
                          backgroundColor: "#fff"
                        }}
                      />
                    ) : (
                      <div className="preview-placeholder">
                        <p>This is how your image will appear in the poster</p>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>

            <div className="crop-modal-footer">
              <button className="secondary-btn" onClick={() => {
                setShowCropModal(false);
                setImageSrc(null);
                setFile(null);
                setCroppedImageUrl(null);
                setCropData(null);
                setIsCropComplete(false);
                setCrop(undefined);
                setCompletedCrop(null);

                // Clear the file input to allow selecting the same file again
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }

                // Trigger the file input click to open the file browser
                setTimeout(() => {
                  document.getElementById('file-input').click();
                }, 100);
              }}>
                <i className="fas fa-redo-alt"></i> Choose another
              </button>
              <button
                className="primary-btn"
                onClick={() => {
                  if (!croppedImageUrl) return;

                  // Store crop data in session storage for use in the poster generation
                  if (cropData) {
                    sessionStorage.setItem('cropData', JSON.stringify(cropData));
                    
                  }

                  // Close crop modal and show preview modal
                  setShowCropModal(false);
                  setShowPreviewModal(true);
                }}
                disabled={!isCropComplete}
              >
                <i className="fas fa-check"></i> Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showPreviewModal && (
        <div className="modal-overlay">
          <div className="preview-modal">
            <div className="preview-modal-header">
              <h2>Preview Your Photo</h2>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowPreviewModal(false);
                            // Re-enable body scroll
      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');
    document.documentElement.classList.remove('modal-open');
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="preview-modal-body">
              <p className="preview-description">
                <i className="fas fa-info-circle"></i> This is how your image will appear in the poster
              </p>
              <div className="preview-image-frame">
                {croppedImageUrl ? (
                  <img
                    src={croppedImageUrl}
                    alt="Preview"
                    className="final-preview-image"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      objectPosition: "center center"
                    }}
                  />
                ) : (
                  <div className="preview-placeholder">
                    <p>Image not available</p>
                  </div>
                )}
              </div>
            </div>
            <div className="preview-modal-footer">
              <div className="preview-modal-buttons">
                <button
                  className="retake-btn"
                  onClick={() => {
                    setShowPreviewModal(false);
                    setShowCropModal(true);
                    // Keep modal-open class since we're switching to crop modal
                  }}
                >
                  <i className="fas fa-crop-alt"></i> Edit Crop
                </button>
                <button
                  className="confirm-btn"
                  onClick={startProcessing}
                >
                  <i className="fas fa-arrow-right"></i> Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Processing Modal */}
      {showProcessingModal && (
        <div className="processing-modal-overlay">
          <div className="processing-modal">
            <div className="processing-content">
              <Loader fullScreen={false} message={''} />
              <div className="processing-text">Generating your poster...</div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPhoto;